import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { sizeMap, BUTTON_MODE } from './interface';
import { spacing, colors, height, typographyScale, radius, palette } from 'common/theme';
import { TypographyVariant } from 'common/theme/tokens/typography/scale';
import { FontWeight } from 'common/theme/tokens/typography/fontWeight';

export const useButton = (mode, variant, size = 'm', isActive = false) => {
  const theme = useTheme();

  const variantColors = useMemo(() => {
    const variants = {
      primary: {
        button: theme.palette.primary.main,
        text: theme.palette.primary.contrastText,
        hover: theme.palette.primary.dark,
      },
      secondary: {
        button: theme.palette.secondary.main,
        text: theme.palette.secondary.contrastText,
        hover: theme.palette.secondary.dark,
      },
      error: {
        button: theme.palette.error.main,
        text: colors.palette.white,
        hover: theme.palette.error.dark || colors.palette.red600,
      },
      success: {
        button: theme.palette.success?.main || colors.palette.green500,
        text: colors.palette.white,
        hover: theme.palette.success?.dark || colors.palette.green600,
      },
    };
    return variants[variant] || variants.primary;
  }, [theme, variant]);

  const modeStyles = useMemo(() => {
    switch (mode) {
      case BUTTON_MODE.CONTAINED:
        return {
          backgroundColor: variantColors.button,
          color: variantColors.text,
          borderRadius: `${radius.md}px`,
          '&:hover': {
            backgroundColor: variantColors.hover,
          },
        };
      case BUTTON_MODE.OUTLINED:
        return {
          backgroundColor: 'transparent',
          color: variantColors.button,
          border: `1px solid ${variantColors.button}`,
          '&:hover': {
            backgroundColor: `${variantColors.button}10`,
            borderColor: variantColors.hover,
          },
        };
      case BUTTON_MODE.TEXT:
        return {
          backgroundColor: 'transparent',
          fontWeight: FontWeight.bold,
          color: variantColors.button,
          '&:hover': {
            backgroundColor: `${variantColors.button}10`,
          },
        };
      case BUTTON_MODE.OUTLINED_CHIP:
        return {
          backgroundColor: colors.palette.white,
          color: colors.system.text.primary,
          border: `1px solid ${colors.palette.blueAlpha200}`,
          '&:hover': {
            backgroundColor: colors.palette.blue200,
            borderColor: colors.palette.blue550,
          },
        };
      case BUTTON_MODE.MENU:
        return {
          display: 'flex',
          flexDirection: 'row',
          textAlign: 'left',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: `${spacing.md}px ${spacing.md}px`,
          gap: `${spacing.md}px`,
          height: height.xxl,
          width: '100%',
          minWidth: 'auto',
          backgroundColor: isActive ? palette.blue400 : colors.system.surface,
          color: colors.system.text.primary,
          borderRadius: 0,
          fontSize: typographyScale[TypographyVariant.TitleLarge].fontSize,
          fontWeight: isActive ? 700 : 400,
          flex: 'none',
          alignSelf: 'stretch',
          borderRadius: `${radius.md}px`,
          flexGrow: 0,
          '&:hover': {
            backgroundColor: isActive ? colors.system.surface.subtle : colors.system.surface,
            fontWeight: 700,
          },
          '&:last-child': {
            borderBottom: 'none',
          },
        };
      case BUTTON_MODE.TAB:
        return {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          padding: `${spacing.md}px`,
          gap: `${spacing.sm}px`,
          height: height.xl,
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          backgroundColor: isActive ? colors.palette.blue400 : colors.palette.blue300,
          color: colors.system.text.primary,
          borderRadius: isActive ? `${radius.md}px` : 0,
          fontSize: typographyScale[TypographyVariant.BodyLarge].fontSize,
          borderBottom: `2px solid ${colors.system.container.secondary}`,
          flexShrink: 0,
          '&:hover': {
            backgroundColor: colors.palette.blue550,
            color: colors.palette.white,
          },
        };
      default:
        return {};
    }
  }, [mode, variantColors, isActive]);

  const sizeStyles = useMemo(() => {
    const sizes = sizeMap[size] || sizeMap.m;
    return {
      height: sizes.height,
      fontSize: sizes.fontSize,
      padding: sizes.padding,
    };
  }, [size]);

  return {
    modeStyles,
    sizeStyles,
    variantColors,
  };
};

